import {createAction, createSlice} from '@reduxjs/toolkit';
import {
  CombinedLeasingObjectDataByMake,
  CombinedLeasingObjectTypesAndMakes,
  LeasingCalculatorGeneralDataCodes,
  UwtType,
} from 'modules/types/LeasingCalculatorCodes';
import {
  fetchCalculatorRemainingValue,
  fetchCombinedLeasingObjectDataByMake,
  fetchCombinedLeasingObjectTypesAndMakes,
  fetchLeasingCalculatorCodes,
  fetchLeasingCalculatorGeneralDataCodes,
} from './thunks';

interface LeasingCalculatorCodesState<ResponseType> {
  data?: ResponseType;
  loading?: boolean;
  error?: string;
}

export interface LeasingCalcualtorCodesState {
  generalDataCodes?: LeasingCalculatorCodesState<LeasingCalculatorGeneralDataCodes>;
  leasingObjectTypesAndMakes?: LeasingCalculatorCodesState<
    CombinedLeasingObjectTypesAndMakes[]
  >;
  leasingObjectDataByMake?: LeasingCalculatorCodesState<
    CombinedLeasingObjectDataByMake[]
  >;
  leasingTaxRateCodes?: UwtType[];
  financingPeriods?: UwtType[];
  paymentPeriodTypes?: UwtType[];
  leasingObjectFirstRegistrationYears?: UwtType[];
  leasingObjectAgeCodes?: UwtType[];
  loading?: boolean;
  error?: string;
  remainingValue?: {percentage: number, value: number};
}

const initialCodesState = {
  data: undefined,
  loading: true,
  error: undefined,
};

export const initialState: LeasingCalcualtorCodesState = {
  generalDataCodes: initialCodesState,
  leasingObjectTypesAndMakes: initialCodesState,
  leasingObjectDataByMake: initialCodesState,
  leasingTaxRateCodes: undefined,
  financingPeriods: undefined,
  paymentPeriodTypes: undefined,
  leasingObjectFirstRegistrationYears: undefined,
  leasingObjectAgeCodes: undefined,
  loading: false,
  error: undefined,
  remainingValue: undefined,
};

export const setRemainingValue = createAction<{percentage: number, value: number}>('setRemainingValue');

export const leasingCalculatorCodesSlice = createSlice({
  name: 'leasingCalculatorCodes',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(fetchLeasingCalculatorCodes.pending, (state) => {
      state.loading = true;
      state.error = initialState.error;
    });
    builder.addCase(fetchLeasingCalculatorCodes.fulfilled, (state, action) => {
      state.loading = false;
      state.leasingTaxRateCodes = action.payload.leasingTaxRateCodes;
      state.financingPeriods = action.payload.financingPeriods;
      state.paymentPeriodTypes = action.payload.paymentPeriodTypes;
      state.leasingObjectFirstRegistrationYears =
        action.payload.leasingObjectFirstRegistrationYears;
      state.leasingObjectAgeCodes = action.payload.leasingObjectAgeCodes;
    });
    builder.addCase(fetchLeasingCalculatorCodes.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    builder.addCase(
      fetchLeasingCalculatorGeneralDataCodes.pending,
      (state) => ({
        ...state,
        generalDataCodes: {
          ...state.generalDataCodes,
          loading: initialState.generalDataCodes?.loading,
          error: initialState.generalDataCodes?.error,
        },
      }),
    );
    builder.addCase(
      fetchLeasingCalculatorGeneralDataCodes.fulfilled,
      (state, action) => ({
        ...state,
        generalDataCodes: {
          ...state.generalDataCodes,
          loading: false,
          data: action.payload,
        },
      }),
    );

    builder.addCase(
      fetchLeasingCalculatorGeneralDataCodes.rejected,
      (state, action) => ({
        ...state,
        generalDataCodes: {
          ...state.generalDataCodes,
          loading: false,
          error: action.payload
            ? (action.payload as Error)?.message
            : action.error?.message,
        },
      }),
    );

    builder.addCase(
      fetchCombinedLeasingObjectTypesAndMakes.pending,
      (state) => {
        if (state.leasingObjectTypesAndMakes) {
          state.leasingObjectTypesAndMakes.loading =
            initialState.leasingObjectTypesAndMakes?.loading;
          state.leasingObjectTypesAndMakes.error =
            initialState.leasingObjectTypesAndMakes?.error;
        }
      },
    );
    builder.addCase(
      fetchCombinedLeasingObjectTypesAndMakes.fulfilled,
      (state, action) => {
        if (state.leasingObjectTypesAndMakes) {
          state.leasingObjectTypesAndMakes.loading = false;
          state.leasingObjectTypesAndMakes.data = action.payload;
        }
      },
    );
    builder.addCase(
      fetchCombinedLeasingObjectTypesAndMakes.rejected,
      (state, action) => {
        if (state.leasingObjectTypesAndMakes) {
          state.leasingObjectTypesAndMakes.loading = false;
          state.leasingObjectTypesAndMakes.error = action.payload
            ? (action.payload as Error)?.message
            : action.error?.message;
        }
      },
    );

    builder.addCase(fetchCombinedLeasingObjectDataByMake.pending, (state) => {
      if (state.leasingObjectDataByMake) {
        state.leasingObjectDataByMake.loading =
          initialState.leasingObjectDataByMake?.loading;
        state.leasingObjectDataByMake.error =
          initialState.leasingObjectDataByMake?.error;
      }
    });
    builder.addCase(
      fetchCombinedLeasingObjectDataByMake.fulfilled,
      (state, action) => {
        if (state.leasingObjectDataByMake) {
          state.leasingObjectDataByMake.loading = false;
          state.leasingObjectDataByMake.data = action.payload;
        }
      },
    );
    builder.addCase(
      fetchCombinedLeasingObjectDataByMake.rejected,
      (state, action) => {
        if (state.leasingObjectDataByMake) {
          state.leasingObjectDataByMake.loading = false;
          state.leasingObjectDataByMake.error = action.payload
            ? (action.payload as Error)?.message
            : action.error?.message;
        }
      },
    );
    builder.addCase(
        fetchCalculatorRemainingValue.fulfilled,
        (state, action) => {
            state.remainingValue = {percentage: action.payload.percentage, value: action.payload.value}
        },
    );
    builder.addCase(setRemainingValue, (state, action) => {
      state.remainingValue = action.payload;
    });
  },
});

export default leasingCalculatorCodesSlice.reducer;
